// src/components/orders/OrderPaymentModal.jsx
import React, { useState, useEffect } from 'react';
import {
    Modal, Form, InputNumber, Select, DatePicker, Input,
    Descriptions, Tag, Divider, message, Spin, Alert, Segmented
} from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { addOrderPayment, getOrderPayments } from '../../services/pedidosService';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/es_ES';

const { Option } = Select;
const { TextArea } = Input;

/**
 * OrderPaymentModal — Register a payment (partial, full, on-delivery) for a purchase order.
 *
 * Descuento Proveedor:
 *   - Campo "Descuento Proveedor" (%) debajo de "Método de Pago".
 *   - Disponible en los dos modos del selector (PAGO TOTAL / OTRO MONTO).
 *   - En PAGO TOTAL el monto se auto-calcula en vivo: (total - descuento) - efectivo pagado.
 *   - En OTRO MONTO el monto es libre (permite pagar de mas y generar Saldo a Favor).
 *   - Se habilita solo mientras la orden NO este liquidada al 100% (cobertura < total).
 *
 * Props:
 *   visible  {boolean}   — Show/hide the modal
 *   order    {object}    — Order row from the table
 *   onClose  {function}  — Called when modal closes
 *   onPaymentRegistered {function} — Called after a successful payment registration
 */
const OrderPaymentModal = ({ visible, order, onClose, onPaymentRegistered }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [paymentsHistory, setPaymentsHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [summary, setSummary] = useState(null);
    const [paymentType, setPaymentType] = useState('TOTAL'); // 'TOTAL' | 'CUSTOM'

    // % de Descuento Proveedor que el usuario esta tipeando (vista previa en vivo)
    const discountPct = Form.useWatch('supplier_discount_percentage', form) ?? 0;

    const formatCurrency = (val) => {
        const n = Number(val) || 0;
        return `$ ${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (date) => {
        if (!date) return '-';
        const d = String(date).split('T')[0];
        const [y, m, day] = d.split('-');
        return `${String(day).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    };

    // Load payment history when modal opens
    useEffect(() => {
        if (visible && order?.id) {
            setLoadingHistory(true);
            getOrderPayments(order.id)
                .then((res) => {
                    if (res?.success) {
                        setSummary(res.data.summary);
                        setPaymentsHistory(res.data.payments || []);
                    }
                })
                .catch(() => {})
                .finally(() => setLoadingHistory(false));
        }
    }, [visible, order?.id]);

    const handleClose = () => {
        form.resetFields();
        setPaymentsHistory([]);
        setSummary(null);
        setPaymentType('TOTAL');
        onClose();
    };

    // ========================================================================
    // Magnitudes de la orden
    // ========================================================================
    // Use fresh summary from API if loaded, fallback to table row data
    const displayTotal    = Number(summary?.order_total   ?? order?.total)       || 0;
    const displayPaid     = Number(summary?.paid_amount    ?? order?.paid_amount) || 0;
    const displayDiscount = Number(summary?.supplier_discount_amount
                                    ?? order?.supplier_discount_amount) || 0;
    const displayNetTotal = displayTotal - displayDiscount;
    const displayPending  = Number(summary?.pending_amount ?? order?.pending_amount
                                    ?? (displayNetTotal - displayPaid)) || 0;
    const displayCredit   = Number(summary?.credit_amount  ?? order?.credit_amount) || 0;
    const displayPaymentStatus = summary?.payment_status ?? order?.payment_status ?? 'PENDIENTE';

    // Cobertura guardada: efectivo pagado + descuento vigente.
    // La regla de habilitacion del campo usa el estado GUARDADO, no la vista previa.
    const savedCoverage = Number(
        summary?.covered_amount ?? order?.covered_amount ?? (displayPaid + displayDiscount)
    ) || 0;
    const discountDisabled = savedCoverage >= displayTotal;

    // Vista previa de lo que el usuario esta tipeando
    const previewDiscount = Math.round(displayTotal * (Number(discountPct) || 0) / 100 * 100) / 100;
    const previewNetTotal = displayTotal - previewDiscount;
    const previewPending  = previewNetTotal - displayPaid;   // puede ser <= 0

    // Cargar el % vigente del descuento en la orden (semantica SET: se reenvia el valor actual)
    useEffect(() => {
        if (visible && summary) {
            const pct = Number(summary.supplier_discount_percentage);
            if (Number.isFinite(pct) && pct > 0) {
                form.setFieldValue('supplier_discount_percentage', pct);
            }
        }
    }, [visible, summary]); // eslint-disable-line

    // Auto-fill amount when paymentType is TOTAL (neto pendiente segun el descuento tipeado)
    useEffect(() => {
        if (paymentType === 'TOTAL') {
            const v = Math.max(0, previewPending);
            form.setFieldValue('amount', v > 0 ? parseFloat(v.toFixed(2)) : undefined);
        }
    }, [paymentType, previewPending]); // eslint-disable-line

    const paymentStatusConfig = {
        PENDIENTE: { color: 'default', label: 'Sin pagar' },
        PARCIAL:   { color: 'purple',  label: 'Parcial'   },
        PAGADO:    { color: 'green',   label: 'Pagado'    },
    };
    const psc = paymentStatusConfig[displayPaymentStatus] || { color: 'default', label: displayPaymentStatus };

    const showApiError = (err) => {
        const data = err?.response?.data;
        if (data?.error_code === 'ORDER_ALREADY_SETTLED') {
            message.warning(data.message || 'La orden ya esta liquidada al 100%.');
            return;
        }
        message.error(data?.message || 'Error al registrar el pago');
    };

    const doSubmit = async (values, cajaConfirmed) => {
        const response = await addOrderPayment(order.id, {
            payment_date: dayjs(values.payment_date).format('YYYY-MM-DD'),
            amount: values.amount,
            payment_method: values.payment_method || null,
            notes: values.notes || null,
            supplier_discount_percentage:
                values.supplier_discount_percentage ?? null,
            caja_confirmed: cajaConfirmed ? true : undefined,
        });

        if (response?.success) {
            message.success('Pago registrado exitosamente');
            form.resetFields();
            onPaymentRegistered?.();
            handleClose();
        } else {
            message.error(response?.message || 'Error al registrar el pago');
        }
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return; // validacion inline — antd muestra los errores en los campos
        }

        setLoading(true);
        try {
            await doSubmit(values, false);
        } catch (err) {
            const data = err?.response?.data;

            if (data?.error_code === 'CAJA_CERRADA') {
                // No hay sesion de Caja abierta: se pide el reconocimiento explicito.
                setLoading(false);
                Modal.confirm({
                    title: 'No hay sesión de Caja abierta',
                    content: (
                        <div>
                            <p>
                                El pago se registra igual: el efectivo sale de la organización y no de un
                                cajón, por lo que la Caja del día no se ve afectada.
                            </p>
                            <p style={{ marginBottom: 0 }}>
                                El egreso de {formatCurrency(values.amount)} queda pendiente de registrar
                                en Caja.
                            </p>
                        </div>
                    ),
                    okText: 'Registrar igual',
                    cancelText: 'Cancelar',
                    onOk: async () => {
                        setLoading(true);
                        try {
                            await doSubmit(values, true);
                        } catch (err2) {
                            showApiError(err2);
                        } finally {
                            setLoading(false);
                        }
                    },
                });
                return;
            }

            showApiError(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={
                <span>
                    <DollarOutlined style={{ marginRight: 8 }} />
                    Registrar Pago — {order?.order_number}
                </span>
            }
            open={visible}
            onCancel={handleClose}
            onOk={handleSubmit}
            okText="Registrar Pago"
            cancelText="Cancelar"
            confirmLoading={loading}
            width={820}
            destroyOnClose
        >
            {/* Order payment summary */}
            <Descriptions size="small" bordered column={3} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Total Orden">
                    <strong>{formatCurrency(displayTotal)}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Descuento Proveedor">
                    <span style={{ color: displayDiscount > 0 ? '#52c41a' : undefined }}>
                        {displayDiscount > 0 ? `−${formatCurrency(displayDiscount)}` : '—'}
                    </span>
                </Descriptions.Item>
                <Descriptions.Item label="Total a Pagar (neto)">
                    <strong>{formatCurrency(displayNetTotal)}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Efectivo Pagado">
                    <span style={{ color: '#52c41a' }}>{formatCurrency(displayPaid)}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Cobertura">
                    <span>{formatCurrency(savedCoverage)}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Pendiente">
                    <span style={{ color: displayPending > 0 ? '#fa8c16' : '#52c41a' }}>
                        {formatCurrency(displayPending)}
                    </span>
                </Descriptions.Item>
                {displayCredit > 0 && (
                    <Descriptions.Item label="Saldo a Favor">
                        <span style={{ color: '#389e0d', fontWeight: 600 }}>
                            {formatCurrency(displayCredit)}
                        </span>
                    </Descriptions.Item>
                )}
            </Descriptions>

            <Tag color={psc.color} style={{ marginBottom: 12 }}>{psc.label}</Tag>

            {displayPaymentStatus !== 'PAGADO' && (
                <Segmented
                    block
                    value={paymentType}
                    onChange={(v) => {
                        setPaymentType(v);
                        if (v === 'TOTAL') {
                            const p = Math.max(0, previewPending);
                            form.setFieldValue('amount', p > 0 ? parseFloat(p.toFixed(2)) : undefined);
                        } else {
                            form.setFieldValue('amount', undefined);
                        }
                    }}
                    options={[
                        { label: '✅ Pago Total', value: 'TOTAL' },
                        { label: '💰 Otro Monto', value: 'CUSTOM' },
                    ]}
                    style={{ marginBottom: 16 }}
                />
            )}

            {displayPaymentStatus === 'PAGADO' && (
                <Alert
                    type="success"
                    message="Esta orden ya está completamente pagada."
                    showIcon
                    style={{ marginBottom: 12 }}
                />
            )}

            {/* Payment form */}
            <Form form={form} layout="vertical" size="small">
                <Form.Item
                    name="payment_date"
                    label="Fecha del Pago"
                    rules={[{ required: true, message: 'Seleccione la fecha del pago' }]}
                    initialValue={dayjs()}
                >
                    <DatePicker
                        locale={locale}
                        format="DD/MM/YYYY"
                        style={{ width: '100%' }}
                        placeholder="Seleccione fecha"
                    />
                </Form.Item>

                <Form.Item
                    name="amount"
                    label={paymentType === 'TOTAL' ? 'Monto a Pagar (total pendiente)' : 'Otro Monto'}
                    rules={[
                        { required: true, message: 'Ingrese el monto' },
                        { type: 'number', min: 0.01, message: 'El monto debe ser mayor a cero' },
                    ]}
                    initialValue={displayPending > 0 ? parseFloat(displayPending.toFixed(2)) : undefined}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        min={0.01}
                        precision={2}
                        addonBefore="$"
                        placeholder="0.00"
                        disabled={paymentType === 'TOTAL'}
                    />
                </Form.Item>

                <Form.Item
                    name="payment_method"
                    label="Método de Pago"
                    rules={[{ required: true, message: 'Seleccione el método de pago' }]}
                    initialValue="Efectivo"
                >
                    <Select placeholder="Seleccione método" defaultValue="Efectivo">
                        <Option value="Efectivo">Efectivo</Option>
                        <Option value="Transferencia Bancaria">Transferencia Bancaria</Option>
                        <Option value="Cheque">Cheque</Option>
                        <Option value="Cuenta Corriente">Cuenta Corriente</Option>
                        <Option value="Otros medios de pago electrónico">Otros medios de pago electrónico</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="supplier_discount_percentage"
                    label="Descuento Proveedor"
                    rules={[
                        { type: 'number', min: 0, max: 99.99, message: 'El descuento debe estar entre 0 y 99,99 %' },
                        ...(paymentType === 'TOTAL' ? [{
                            validator: async (_, value) => {
                                const pct = Number(value) || 0;
                                const d = Math.round(displayTotal * pct / 100 * 100) / 100;
                                if ((displayTotal - d) - displayPaid <= 0) {
                                    throw new Error(
                                        `El descuento no puede superar el saldo pendiente (${formatCurrency(displayTotal - displayPaid)})`
                                    );
                                }
                            }
                        }] : []),
                    ]}
                    extra={
                        discountDisabled
                            ? 'La orden ya está liquidada al 100%: el descuento solo puede aplicarse antes de la liquidación.'
                            : paymentType === 'CUSTOM' && previewPending <= 0
                                ? `Con este descuento la orden quedaría saldada y se generaría un saldo a favor de ${formatCurrency(savedCoverage + previewDiscount - displayTotal)}.`
                                : `Sobre el total de la orden (${formatCurrency(displayTotal)}). Descuento: ${formatCurrency(previewDiscount)} — Total a pagar: ${formatCurrency(previewNetTotal)}`
                    }
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        max={99.99}
                        precision={2}
                        addonAfter="%"
                        placeholder="0.00"
                        disabled={discountDisabled}
                    />
                </Form.Item>

                <Form.Item name="notes" label="Nota (opcional)">
                    <TextArea rows={2} maxLength={500} placeholder="Ej: Pago contraentrega — remito #12345" />
                </Form.Item>
            </Form>

            {/* Payment history */}
            {(loadingHistory || paymentsHistory.length > 0) && (
                <>
                    <Divider orientation="left" style={{ margin: '12px 0 8px 0' }}>
                        Historial de Pagos
                    </Divider>
                    {loadingHistory ? (
                        <Spin size="small" />
                    ) : (
                        <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                            {paymentsHistory.map((p) => (
                                <div
                                    key={p.id}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '4px 0',
                                        borderBottom: '1px solid #f0f0f0',
                                        fontSize: 12,
                                    }}
                                >
                                    <span style={{ color: '#595959' }}>{formatDate(p.payment_date)}</span>
                                    <span>{p.payment_method || '—'}</span>
                                    {Number(p.supplier_discount_amount) > 0 && (
                                        <span style={{ color: '#52c41a' }}>
                                            dto −{formatCurrency(p.supplier_discount_amount)}
                                        </span>
                                    )}
                                    <span style={{ fontWeight: 600, color: '#52c41a' }}>{formatCurrency(p.amount)}</span>
                                    {p.caja_status === 'EFECTIVO_FUERA_DE_CAJA' && (
                                        <span style={{ color: '#fa8c16' }} title="Efectivo fuera de Caja">
                                            fuera de caja
                                        </span>
                                    )}
                                    <span style={{ color: '#8c8c8c', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {p.notes || ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

export default OrderPaymentModal;
